import { icon } from '@carvana/ds-styles';
import { Info } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { absoluteBaseUrl } from '~/constants/environment';
import {
  useDealershipConfiguration,
  useDealershipDepartmentHours,
  useDealershipId
} from '~/context/dealership/StaticContext';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import {
  Divider,
  Footer,
  Heading,
  Hours,
  HoursHeading,
  LinksOuterWrapper,
  LinksWrapper
} from './PageFooter.styles';

function getLegalLinks(dealershipId: string | null) {
  return [
    {
      href: buildDealershipUrl(`${absoluteBaseUrl}/terms-of-use`, dealershipId),
      text: 'User Agreement'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/financial-and-other-privacy-notices`,
        dealershipId
      ),
      text: 'Financial and Other Privacy Notices'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/privacy-policy`,
        dealershipId
      ),
      text: 'Consumer Privacy Notice'
    },
    {
      href: 'https://privacy.carvana.com/dont_sell',
      text: 'Do Not Sell or Share My Personal Information'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/code-of-conduct/`,
        dealershipId
      ),
      text: 'Code of Conduct'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/responsible-disclosure`,
        dealershipId
      ),
      text: 'Responsible Disclosure'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/accessibility`,
        dealershipId
      ),
      text: 'Accessibility'
    }
  ];
}

export default function PageFooter() {
  const dealershipId = useDealershipId();
  const { name } = useDealershipConfiguration();
  const departmentHours = useDealershipDepartmentHours();
  const legalLinks = useMemo(() => getLegalLinks(dealershipId), [dealershipId]);

  return (
    <Footer>
      <div className="flex flex-col gap-y-4xl md:grid md:grid-cols-3 lg:grid-cols-[1fr_2fr_1fr]">
        <LinksOuterWrapper>
          <LinksWrapper>
            <Heading>Financing</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financing?openSoftpullModal=true`,
                dealershipId,
                'softpull'
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="get-pre-qualified"
              data-analytics-id={null}
              data-testid="get-pre-qualified"
            >
              Get Pre-Qualified
            </a>
          </LinksWrapper>

          <LinksWrapper>
            <Heading>Trade/Sell</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/sell-my-car`,
                dealershipId,
                'stc'
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="sell-my-car"
              data-analytics-id={null}
              data-testid="get-an-offer"
            >
              Get an Offer
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/value-tracker`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="value-tracker"
              data-analytics-id={null}
              data-testid="value-tracker"
            >
              Carvana Value Tracker
            </a>
          </LinksWrapper>
        </LinksOuterWrapper>

        <LinksOuterWrapper className="lg:grid lg:grid-cols-2">
          <LinksWrapper>
            <Heading>About {name}</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/about-us`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="about-us"
              data-analytics-id={null}
              data-testid="about-us"
            >
              About Us
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/reviews`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="customer-reviews"
              data-analytics-id={null}
              data-testid="customer-reviews"
            >
              Customer Reviews
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/careers`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="careers"
              data-analytics-id={null}
              data-testid="careers"
            >
              Careers
            </a>
          </LinksWrapper>

          <LinksWrapper>
            <Heading>Support</Heading>
            <a
              href={buildDealershipUrl(`${absoluteBaseUrl}/help`, dealershipId)}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-x-xl"
              data-adobe-id="support-and-contact"
              data-analytics-id={null}
              data-testid="support-and-contact"
            >
              <Info size={32} color={icon.primarySubtle} />
              Support &amp; Contact
            </a>
          </LinksWrapper>
        </LinksOuterWrapper>

        <LinksWrapper>
          <Heading>Hours</Heading>

          {departmentHours?.map((department, index) => (
            <Fragment key={department.label}>
              {index > 0 && <Divider />}

              <HoursHeading>{department.label}</HoursHeading>
              <Hours>
                {department.daysHours.map(({ days, hours }) => (
                  <Fragment key={`${department.label}-${days}`}>
                    <div>{days}</div>
                    <div className="text-right">{hours}</div>
                  </Fragment>
                ))}
              </Hours>
            </Fragment>
          ))}
        </LinksWrapper>
      </div>

      <Divider />

      <div className="flex flex-col gap-y-xl">
        <p>
          Copyright © {new Date().getFullYear()} Carvana, LLC. All Rights
          Reserved.
        </p>
        <p className="flex flex-wrap gap-x-sm">
          {legalLinks.map(({ href, text }, index) => (
            <Fragment key={text}>
              {index > 0 && ' | '}
              <a
                href={href}
                target="_blank"
                rel="noopener"
                data-adobe-id={toLowerKebabCase(text)}
                data-analytics-id={null}
                data-testid="legal-link"
              >
                {text}
              </a>
            </Fragment>
          ))}
        </p>
      </div>
    </Footer>
  );
}
